'use client';

// ─── Types ────────────────────────────────────────────────────────────────────

export type OperationType = 'create' | 'update' | 'delete';
export type OperationStatus = 'pending' | 'syncing' | 'synced' | 'failed' | 'conflict';

export interface QueuedOperation {
  operationId: string;       // Unique operation ID (prevents duplicate processing)
  type: OperationType;
  table: string;             // Supabase table name
  module: string;            // Human-readable module name
  recordId: string;          // The record's own ID
  data: any;                 // Full record data
  userId: string;
  createdAt: string;
  updatedAt: string;         // For conflict detection
  attempts: number;
  maxAttempts: number;
  status: OperationStatus;
  lastError?: string;
  nextRetryAt?: string;
}

export type ConnectionStatus = 'online' | 'offline' | 'syncing';

// ─── Constants ────────────────────────────────────────────────────────────────

const QUEUE_KEY = 'ega_offline_queue';
const MAX_ATTEMPTS = 8;
const BASE_DELAY_MS = 2000; // 2s base

// ─── Unique ID generator (crypto-safe) ───────────────────────────────────────

export function generateOperationId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback: timestamp + random bytes
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 10);
  const rand2 = Math.random().toString(36).slice(2, 10);
  return `${ts}-${rand}-${rand2}`;
}

// ─── Exponential backoff ──────────────────────────────────────────────────────

export function getNextRetryDelay(attempts: number): number {
  // 2s, 4s, 8s, 16s, 32s, 64s, 128s, 256s (capped at ~4 min)
  return Math.min(BASE_DELAY_MS * Math.pow(2, attempts), 256_000);
}

// ─── Queue storage ────────────────────────────────────────────────────────────

export function loadQueue(): QueuedOperation[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as QueuedOperation[];
  } catch {
    return [];
  }
}

export function saveQueue(queue: QueuedOperation[]): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch (e) {
    console.warn('[OfflineQueue] Could not save queue to localStorage', e);
  }
}

// ─── Queue operations ─────────────────────────────────────────────────────────

export function enqueueOperation(op: Omit<QueuedOperation, 'operationId' | 'attempts' | 'status' | 'createdAt'>): QueuedOperation {
  const queue = loadQueue();

  // Idempotency: if same recordId + type already pending, update it instead of adding duplicate
  const existingIdx = queue.findIndex(
    (q) => q.recordId === op.recordId && q.type === op.type && q.status === 'pending' && q.userId === op.userId
  );

  const newOp: QueuedOperation = {
    ...op,
    operationId: generateOperationId(),
    attempts: 0,
    status: 'pending',
    createdAt: new Date().toISOString(),
    maxAttempts: op.maxAttempts ?? MAX_ATTEMPTS,
  };

  if (existingIdx >= 0) {
    // Replace with updated data (idempotent update)
    queue[existingIdx] = { ...queue[existingIdx], data: op.data, updatedAt: op.updatedAt };
    saveQueue(queue);
    return queue[existingIdx];
  }

  queue.push(newOp);
  saveQueue(queue);
  return newOp;
}

export function dequeueOperation(operationId: string): void {
  const queue = loadQueue().filter((q) => q.operationId !== operationId);
  saveQueue(queue);
}

export function markOperationSynced(operationId: string): void {
  dequeueOperation(operationId);
}

export function markOperationFailed(operationId: string, error: string): void {
  const queue = loadQueue();
  const idx = queue.findIndex((q) => q.operationId === operationId);
  if (idx < 0) return;

  const op = queue[idx];
  op.attempts += 1;
  op.lastError = error;
  op.status = op.attempts >= op.maxAttempts ? 'failed' : 'pending';
  op.nextRetryAt = new Date(Date.now() + getNextRetryDelay(op.attempts)).toISOString();
  queue[idx] = op;
  saveQueue(queue);
}

export function markOperationConflict(operationId: string, error: string): void {
  const queue = loadQueue();
  const idx = queue.findIndex((q) => q.operationId === operationId);
  if (idx < 0) return;
  queue[idx] = { ...queue[idx], status: 'conflict', lastError: error };
  saveQueue(queue);
}

export function getPendingOperations(userId: string): QueuedOperation[] {
  return loadQueue().filter(
    (q) => q.userId === userId && (q.status === 'pending') &&
      (!q.nextRetryAt || new Date(q.nextRetryAt) <= new Date())
  );
}

export function getAllUserOperations(userId: string): QueuedOperation[] {
  return loadQueue().filter((q) => q.userId === userId);
}

export function clearSyncedOperations(userId: string): void {
  const queue = loadQueue().filter((q) => !(q.userId === userId && q.status === 'synced'));
  saveQueue(queue);
}

// ─── Conflict detection ───────────────────────────────────────────────────────

export interface ConflictInfo {
  operationId: string;
  recordId: string;
  table: string;
  module: string;
  localUpdatedAt: string;
  remoteUpdatedAt: string;
  localData: any;
  remoteData: any;
}

export function detectConflict(op: QueuedOperation, remoteRecord: any): ConflictInfo | null {
  if (!remoteRecord?.updated_at && !remoteRecord?.updatedAt) return null;
  const remoteTs = remoteRecord.updated_at || remoteRecord.updatedAt;
  const localTs = op.updatedAt;
  if (!localTs || !remoteTs) return null;

  const localTime = new Date(localTs).getTime();
  const remoteTime = new Date(remoteTs).getTime();

  // If remote was updated AFTER local snapshot was taken → conflict
  if (remoteTime > localTime) {
    return {
      operationId: op.operationId,
      recordId: op.recordId,
      table: op.table,
      module: op.module,
      localUpdatedAt: localTs,
      remoteUpdatedAt: remoteTs,
      localData: op.data,
      remoteData: remoteRecord,
    };
  }
  return null;
}
